import { Link } from "react-router-dom";
import { Check, Sparkles, Zap, Infinity as InfinityIcon, ShieldCheck } from "lucide-react";

/**
 * Public pricing page — accessible without authentication.
 *
 * Purpose: give Stripe (or any other payment processor / app-store reviewer)
 * a shareable URL that plainly shows:
 *   1. What is being sold (Immersive+ digital subscription)
 *   2. The exact price ($14.99 USD / month, recurring)
 *   3. What is included (feature list)
 *   4. Links to Terms of Service, Privacy Policy, Refund Policy, Contact
 *   5. The 18+ nature of the platform
 *
 * NO checkout button — reviewers don't need to actually pay. Instead, a
 * "Create your account" CTA points to /register for real users. The actual
 * purchase flow still lives on the authenticated /upgrade page.
 */
export default function PublicPricing() {
  return (
    <div className="min-h-[100dvh] bg-[#0B0A10] text-white">
      {/* Ambient gradient */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-violet-600/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-rose-600/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-2xl px-6 py-10">
        {/* Simple public header */}
        <header className="flex items-center justify-between">
          <Link to="/" className="font-display text-lg tracking-tight text-white/90" data-testid="pricing-brand-link">
            Immersive<span className="text-violet-400">.</span>
          </Link>
          <span className="nsfw-badge" data-testid="pricing-18plus-badge">18+</span>
        </header>

        {/* Product intro */}
        <div className="mt-10 text-center">
          <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border border-violet-400/30 px-4 py-1.5 text-xs uppercase tracking-widest text-violet-200">
            <Sparkles size={14} /> Immersive+
          </div>
          <h1 className="mt-5 font-display text-5xl leading-tight text-white" data-testid="pricing-title">
            Unlimited <br />
            <em className="not-italic bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">stories</em>.
          </h1>
          <p className="mt-4 max-w-md mx-auto text-neutral-400 leading-relaxed">
            Immersive+ is our optional monthly subscription that removes limits and unlocks premium features on Immersive Character AI — an adult (18+) AI-powered roleplay chat platform.
          </p>
        </div>

        {/* Price card — identical to the /upgrade card so what reviewers
            see matches what paying users see. */}
        <div className="mt-10 rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent p-6" data-testid="pricing-card">
          <div className="text-center">
            <div className="font-display text-6xl text-white" data-testid="pricing-amount">
              $14.99
              <span className="font-body text-sm text-neutral-500 ml-1">USD / month</span>
            </div>
            <div className="mt-2 text-xs text-neutral-500">
              Recurring subscription. Cancel anytime from your account settings.
            </div>
          </div>

          <ul className="mt-6 space-y-3 text-sm" data-testid="pricing-features">
            <Perk icon={<InfinityIcon size={14} />} text="Unlimited chatting (free plan: 30 messages / day)" />
            <Perk icon={<Sparkles size={14} />} text="Unlimited characters (free plan: 2)" />
            <Perk icon={<Sparkles size={14} />} text="Unlimited personas (free plan: 2)" />
            <Perk icon={<Zap size={14} />} text="Unlimited story recaps & regenerations" />
            <Perk icon={<Zap size={14} />} text="Priority AI response streaming" />
            <Perk icon={<Sparkles size={14} />} text="Full adult (18+) roleplay features" />
            <Perk icon={<Sparkles size={14} />} text="Chat export to PDF, HTML, and Markdown" />
          </ul>

          <Link
            to="/register"
            data-testid="pricing-signup-cta"
            className="btn-pill btn-primary mt-8 flex w-full items-center justify-center py-3.5"
          >
            Create your account to subscribe
          </Link>
          <div className="mt-3 text-center text-xs text-neutral-500">
            Already a member? <Link to="/login" className="text-violet-300 hover:text-violet-200" data-testid="pricing-login-link">Sign in</Link>
          </div>
        </div>

        {/* What you're actually getting — expanded description for reviewers */}
        <div className="mt-10 rounded-3xl border border-white/10 bg-white/[0.02] p-6" data-testid="pricing-what-you-get">
          <h2 className="font-display text-2xl text-white">What is Immersive Character AI?</h2>
          <p className="mt-3 text-sm text-neutral-300 leading-relaxed">
            Immersive Character AI is a digital roleplay chat service where adults can create and interact with fictional AI characters. Conversations are text-based, streamed in real time by Anthropic's Claude Sonnet model, and stored privately in each user's account. All content is fictional and no interactions occur with real humans.
          </p>
          <h3 className="mt-6 font-display text-lg text-white">What Immersive+ unlocks</h3>
          <ul className="mt-3 space-y-2 text-sm text-neutral-300 leading-relaxed list-disc pl-5">
            <li>Higher daily/monthly limits (unlimited messages instead of 30/day, unlimited character &amp; persona slots instead of 2 each).</li>
            <li>Advanced tools: monthly story recaps, message regenerations, per-chat exports.</li>
            <li>Priority streaming and access to premium AI models.</li>
            <li>Full library of adult (18+) roleplay tone options.</li>
          </ul>
          <h3 className="mt-6 font-display text-lg text-white">Age &amp; content policy</h3>
          <p className="mt-2 text-sm text-neutral-300 leading-relaxed flex gap-2 items-start">
            <ShieldCheck size={16} className="mt-0.5 shrink-0 text-violet-300" />
            <span>Users must be 18+ to create an account. Adult content is opt-in per character, and every user passes an age verification step before any adult content is unlocked.</span>
          </p>
        </div>

        {/* Legal / policy links — Stripe wants these visible from the product page */}
        <div className="mt-10 text-center text-xs text-neutral-500" data-testid="pricing-legal-links">
          <p>Payments processed securely by Stripe. Digital service — no physical shipment.</p>
          <div className="mt-3 flex flex-wrap justify-center gap-x-3 gap-y-1">
            <Link to="/terms" className="hover:text-violet-300" data-testid="pricing-terms-link">Terms of Service</Link>
            <span className="text-neutral-700">·</span>
            <Link to="/privacy" className="hover:text-violet-300" data-testid="pricing-privacy-link">Privacy Policy</Link>
            <span className="text-neutral-700">·</span>
            <Link to="/refunds" className="hover:text-violet-300" data-testid="pricing-refunds-link">Refund Policy</Link>
            <span className="text-neutral-700">·</span>
            <Link to="/contact" className="hover:text-violet-300" data-testid="pricing-contact-link">Contact</Link>
          </div>
          <p className="mt-4 text-neutral-600">© {new Date().getFullYear()} Immersive Character AI · imrsvprtl.com</p>
        </div>
      </div>
    </div>
  );
}

function Perk({ icon, text }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/15 text-violet-200">
        {icon}
      </span>
      <span className="text-neutral-200">{text}</span>
    </li>
  );
}
